import vue from "vue";
import { HotTable } from "@handsontable/vue";

import {
  defaultSettings,
  menu,
  afterChange,
  colHeaders,
  columnsRenderer,
  afterSelectionEnd,
  afterDeselect,
  beforeColumnSort,
  afterColumnSort,
  afterViewportRowCalc
} from "./hooks.js";
import { toolsItems } from "./tools.js";

import BookTableFilter from "@/components/book/book-filter/book-filter";
import BookTableSortTree from "@/components/book/book-sort-tree/book-sort-tree";

import cloneDeep from "lodash.clonedeep";
import moment from "moment";
import { notifyFactory, errorParser, openObjectInfo } from "@/utils";
import variables from "@/styles/variables.scss";

import * as numbro from "numbro";
import numbroRu from "numbro/languages/ru-RU";

numbro.registerLanguage(numbroRu);
numbro.setLanguage("ru-RU");

export default {
  components: {
    HotTable,
    BookTableFilter,
    BookTableSortTree
  },
  props: ["active-book"],
  data() {
    return {
      myInstance: null,

      dsInfo: { name: "", geomType: "", length: "" },
      dataLength: null,

      settings: defaultSettings,
      className: "r-table",
      style: "width: auto; height: 100%;",

      isChecked: false,
      selectedRowsAmount: 0,
      changedRows: {},
      changedRowsNewValue: {},
      hasChanges: false,
      needDataUpdate: false,

      bookTableFilter: false,
      bookTableSort: false,

      activeObjectRow: null,

      isUndoAvailable: false,
      isRedoAvailable: false,

      primaryColor: variables.primaryColor,
      toolsItems
    };
  },
  watch: {
    "$store.state.book.activeTable": {
      handler: async function(value) {
        if (!value.updated) return;
        this.changedRows = cloneDeep({});

        if (value.data) {
          this.dsInfo.name = value.name;
          this.dsInfo.geomType = this.getGeomType();
          this.dsInfo.length = value.dataLength;
          this.setActiveFields();

          this.dataLength = Object.keys(value.data).length;
          this.setTableData(value);
        }
        value.updated = false;
      },
      deep: true,
      immediate: true
    },
    "$store.state.book.socketData": {
      handler: function(value) {
        if (!value.action) return;
        if (value.data) this.updateTableData(value.data, value.action);

        value.action = null;
      },
      deep: true,
      immediate: true
    },
    "$store.state.book.activeFields": {
      handler: function(value) {
        if (value.update) {
          const data = this.$store.state.book.activeTable;

          this.dataLength = Object.keys(data.data).length;
          this.setTableData(data);
        }
      },
      deep: true,
      immediate: true
    },
    changedRows: {
      handler: function() {
        this.observeChanges();
      },
      deep: true
    },
    hasActiveCards() {
      this.tableRerender();
    },
    activeObjectInfo: {
      handler: function(val) {
        if (!val || this.isLoading) {
          this.activeObjectRow = null;
          return;
        }
        this.selectRowById(val);
      },
      immediate: true
    },
    isLoading(val) {
      if (val || !this.activeObjectInfo) return;
      this.selectRowById(this.activeObjectInfo);
    }
  },
  computed: {
    activeObjectInfo() {
      return this.$store.state.commonData.activeCard.book;
    },
    isLoading() {
      return this.$store.state.book.tableLoading;
    },
    theme() {
      return this.$store.state.theme;
    },
    hasEditor() {
      return this.$store.getters.hasInstrument("Book", "Editor");
    },
    hasFilters() {
      return this.$store.getters.hasInstrument("Book", "Filters");
    },
    hasColumnsSettings() {
      return this.$store.getters.hasInstrument("Book", "Columns Settings");
    },
    availableTools() {
      return toolsItems.filter(e => {
        if (e.name === "filters" && !this.hasFilters) return false;
        if (e.name === "sorting" && !this.hasColumnsSettings) return false;
        return true;
      });
    },
    hasActiveCards() {
      return !!this.$store.state.commonData.activeCards.book.length;
    },
    existingFilters() {
      return !!this.$store.state.book.filters.filtersConfigs.find(
        e => e.active
      );
    },
    iconName() {
      switch (this.dsInfo.geomType) {
        case "LineString":
        case "MultiLineString":
          return "line";
        case "Polygon":
        case "MultiPolygon":
          return "polygon";
        case "Point":
        case "MultiPoint":
          return "node";
        default:
          return "layers-folder";
      }
    }
  },
  methods: {
    selectRowById(id, noScroll) {
      if (!id || !this.myInstance) return;
      const arrayOfIds = this.myInstance.getDataAtCol(0);
      this.activeObjectRow = arrayOfIds.indexOf(id);
      if (this.activeObjectRow === -1) return;
      this.tableRerender();
      if (!noScroll) this.scrollToRow(this.activeObjectRow);
    },
    scrollToRow(row) {
      const plugin = this.myInstance.getPlugin("autoRowSize");
      const firstRow = plugin.getFirstVisibleRow();
      const lastRow = plugin.getLastVisibleRow();
      const halfOfDifference = Math.trunc((lastRow - firstRow) / 2);
      let rowToScroll =
        row < firstRow
          ? (rowToScroll = row - halfOfDifference + 2)
          : row > lastRow
          ? (rowToScroll = row + halfOfDifference + 4)
          : row;

      if (rowToScroll < 0) {
        rowToScroll = 0;
      } else if (rowToScroll > this.dataLength - 1) {
        rowToScroll = this.dataLength - 1;
      }

      if (row < firstRow) {
        this.myInstance.scrollViewportTo(rowToScroll, 0, false);
      } else if (row > lastRow) {
        this.myInstance.scrollViewportTo(rowToScroll, 0, true);
      }
    },
    parseDateTime(date) {
      if (!date) return "";
      return moment(date).format("DD/MM/YYYY");
    },
    setTableData(data) {
      if (this.needDataUpdate) {
        this.$store.commit("UPDATE_ACTIVE_TABLE");
        this.needDataUpdate = false;
        this.$store.commit("SET_TABLE_LOADING", false);
        return;
      }
      this.changedRows = {};
      this.changedRowsNewValue = {};
      const activeFields = this.$store.state.book.activeFields.fields;
      let tableHead = ["id"];
      let tableHeadTypes = ["string"];
      let tableDataSchema = { id: null };
      let tableData = [];

      activeFields.forEach(e => {
        tableHead.push(e.title);
        tableHeadTypes.push(e.type);
        tableDataSchema[e.title] = null;
      });
      for (var field in data.data) {
        let row = {};
        tableHead.forEach((e, i) => {
          row[e] =
            tableHeadTypes[i] === "datetime"
              ? this.parseDateTime(data.data[field][e])
              : data.data[field][e];
        });
        tableData.push(row);
      }
      this.settings.colHeaders = colHeaders(this, tableHead);
      this.settings.dataSchema = tableDataSchema;
      this.settings.columns = columnsRenderer(this, activeFields);
      this.settings.data = tableData;
      this.settings.contextMenu = menu(this);
      this.settings.afterChange = afterChange(this);
      this.settings.afterSelectionEnd = afterSelectionEnd(this);
      this.settings.afterDeselect = afterDeselect(this);
      this.settings.beforeColumnSort = beforeColumnSort(this);
      this.settings.afterColumnSort = afterColumnSort(this);
      this.settings.afterViewportRowCalculatorOverride = afterViewportRowCalc(
        this
      );
      this.settings.wordWrap = false;
      this.settings.readOnly = !this.hasEditor;
      // TODO: DICH KAKAYATA
      this.tableRerender();
    },
    updateTableData(data, action) {
      const activeFields = this.$store.state.book.activeFields.fields;
      const rowAmount = this.myInstance.countRows();
      let deletedId = {};
      let tableHead = [];
      activeFields.forEach(e => {
        tableHead.push(e.title);
      });

      switch (action) {
        case "update":
          for (let i = 0; i < rowAmount; i++) {
            let rowId = this.myInstance.getDataAtRowProp(i, "id");

            if (data[rowId]) {
              tableHead.forEach(r => {
                let currentData = this.myInstance.getDataAtRowProp(i, r);
                if (currentData != data[rowId[r]]) {
                  this.observeRowChanges(i, r, data[rowId][r], "");
                  this.myInstance.setDataAtRowProp(i, r, data[rowId][r]);
                }
              });
            }
          }
          break;
        case "create":
          this.myInstance.alter("insert_row", 0);
          this.myInstance.setDataAtCell(0, 0, Object.keys(data)[0]);
          break;
        case "delete":
          for (let key in data) {
            deletedId[data[key]] = "true";
          }
          for (let i = 0; i < rowAmount; i++) {
            let rowId = this.myInstance.getDataAtRowProp(i, "id");

            if (deletedId[rowId]) {
              this.myInstance.alter("remove_row", i);
            }
          }
          break;
      }
      this.needDataUpdate = true;
    },
    toolsClickHandler(item) {
      switch (item) {
        case "undo":
          this.myInstance.undo();
          break;
        case "redo":
          this.myInstance.redo();
          break;
      }
    },
    observeRowChanges(row, prop, oldValue, newValue) {
      const selectedId = this.myInstance.getDataAtRowProp(row, "id");
      if (!this.changedRows[selectedId]) {
        vue.set(this.changedRows, selectedId, {});
        vue.set(this.changedRowsNewValue, selectedId, {});
      }
      let currentRow = this.changedRows[selectedId];

      switch (currentRow[prop]) {
        case undefined:
          this.changedRows[selectedId][prop] = oldValue;
          break;
      }
      this.changedRowsNewValue[selectedId][prop] = newValue;

      switch (currentRow[prop]) {
        case newValue:
          vue.delete(this.changedRows[selectedId], prop);
          break;
        case null:
          if (newValue === "") vue.delete(this.changedRows[selectedId], prop);
          break;
        case "":
          if (newValue === null) vue.delete(this.changedRows[selectedId], prop);
          break;
      }

      if (!Object.keys(this.changedRows[selectedId]).length)
        vue.delete(this.changedRows, selectedId);
    },
    observeChanges() {
      this.isUndoAvailable = this.myInstance.isUndoAvailable();
      this.isRedoAvailable = this.myInstance.isRedoAvailable();
      this.hasChanges = Object.keys(this.changedRows).length ? true : false;
    },
    openObjectInfo(data) {
      const selectedRow = data[0].start.row;
      const selectedId = this.myInstance.getDataAtRowProp(selectedRow, "id");

      openObjectInfo(this, this.activeBook, selectedId, "book");
    },
    async setActiveFields(update = false) {
      if (!this.$store.state.book.activeFields.fields) {
        let activeFields = [];
        this.$store.state.book.activeTable.fields.forEach(e => {
          if (e.title === "id") return;
          let field = {};
          if (
            !e.system_field &&
            e.type !== "has_many" &&
            e.type !== "belongs_to" &&
            e.type !== "has_many_through"
          ) {
            field = {
              title: e.title,
              type: e.type
            };
            field["read_only"] = e.read_only || e.system_field ? true : false;
            activeFields.push(field);
          }
        });
        if (activeFields.length > 10) {
          activeFields = activeFields.splice(0, 9);
        }
        this.$store.commit("SET_ACTIVE_FIELDS", {
          fields: activeFields,
          update: update
        });
      }
      this.$store.commit("SET_TABLE_LOADING", false);
    },
    getRowData(data) {
      const selectedRow = data[0].start.row;
      console.log(this.myInstance.getDataAtRow(selectedRow));
    },
    createNewObject() {
      this.$store.commit("SET_OBJECT_CREATE_SOURCE_ID", this.activeBook);
      this.$store.commit("OPEN_MODAL_WINDOW", "create-object");
    },
    toggleFilter() {
      this.bookTableFilter = !this.bookTableFilter;
    },
    toggleSortTree() {
      this.bookTableSort = !this.bookTableSort;
    },
    beforeCancelChanges() {
      if (!this.hasChanges) {
        this.tableRerender();
        return;
      }

      const warningText = `${this.$t("modal.warning_text::cancel")}`;
      const warningTitle = `${this.$t("modal.warning_title")}`;
      const confirmButtonText = `${this.$t("modal.confirm_button")}`;
      const cancelButtonText = `${this.$t("modal.cancel_button")}`;
      this.$confirm(warningText, warningTitle, {
        customClass: "r-message-box",
        type: "warning",
        closeOnPressEscape: false,
        closeOnClickModal: false,
        confirmButtonText,
        cancelButtonText
      })
        .then(() => {
          this.restoreData();
        })
        .catch(() => {});
    },
    restoreData() {
      const data = this.$store.state.book.activeTable;

      this.dataLength = Object.keys(data.data).length;
      this.setTableData(data);
      this.myInstance.clearUndo();
      this.isUndoAvailable = false;
      this.isRedoAvailable = false;
    },
    beforeDeleteObjects(selectedRowIds) {
      if (!selectedRowIds) {
        let selectedFields = this.myInstance.getSelected();
        selectedRowIds = [];
        // eslint-disable-next-line
        selectedFields.forEach(([startRow, startCol, endRow]) => {
          for (let i = startRow; i <= endRow; i++) {
            selectedRowIds.push(this.myInstance.getDataAtRowProp(i, "id"));
          }
        });
      }

      const warningText = `${this.$t("modal.warning_text::delete")}`;
      const warningTitle = `${this.$t("modal.warning_title")}`;
      const confirmButtonText = `${this.$t("modal.confirm_button")}`;
      const cancelButtonText = `${this.$t("modal.cancel_button")}`;
      this.$confirm(warningText, warningTitle, {
        customClass: "r-message-box",
        type: "warning",
        closeOnPressEscape: false,
        closeOnClickModal: false,
        confirmButtonText,
        cancelButtonText
      })
        .then(() => {
          this.deleteObjects(selectedRowIds);
        })
        .catch(() => {});
    },
    deleteObjects(selectedRowIds) {
      const ids = selectedRowIds.map(item => item).join(",");

      this.$store
        .dispatch("DELETE_REQUEST", {
          url: `objectInfo/${this.activeBook}?id=${ids}`
        })
        .then(() => {
          const message = "Удаление объекта выполнено успешно";
          this.$notify(notifyFactory("success", "Удаление выполнено", message));
          this.$store.commit("UPDATE_ACTIVE_TABLE");
        })
        .catch(e => {
          const message = "Удаление не выполнено. Повторите попытку.";
          const title = "Ошибка";
          errorParser.call(this, e, message, title);
        });
    },
    beforeSaveChanges() {
      if (!this.hasChanges) return;

      const warningText = `${this.$t("modal.warning_text::save")}`;
      const warningTitle = `${this.$t("modal.warning_title")}`;
      const confirmButtonText = `${this.$t("modal.confirm_button")}`;
      const cancelButtonText = `${this.$t("modal.cancel_button")}`;
      this.$confirm(warningText, warningTitle, {
        customClass: "r-message-box",
        type: "warning",
        closeOnPressEscape: false,
        closeOnClickModal: false,
        confirmButtonText,
        cancelButtonText
      })
        .then(() => {
          this.saveChanges();
        })
        .catch(() => {});
    },
    saveChanges() {
      const data = [];
      for (let key in this.changedRows) {
        let modifiedField = this.changedRowsNewValue[key];
        modifiedField["id"] = key;
        data.push({ ...modifiedField, _action: "updated" });
      }

      this.$store
        .dispatch("PUT_REQUEST", {
          url: `objectInfo/${this.activeBook}`,
          data
        })
        .then(() => {
          const message = this.$t("save_success_message");
          this.$notify(
            notifyFactory("success", this.$t("save_success_title"), message)
          );
          this.changedRows = {};
          this.changedRowsNewValue = {};
          this.tableRerender();
        })
        .catch(error => {
          const message = "Сохранение не выполнено. Повторите попытку";
          const title = "Ошибка";
          errorParser.call(this, error, message, title);
        });
    },
    tableRerender() {
      this.$nextTick(() => this.myInstance.render());
      this.$nextTick(() => this.myInstance.render());
      this.$store.commit("SET_TABLE_LOADING", false);
    },
    getGeomType() {
      const { data } = this.$store.state.book.activeTable;
      const dataArray = Object.keys(data);

      if (!dataArray || !dataArray.length) return;

      return data[dataArray[0]] && data[dataArray[0]].geom
        ? data[dataArray[0]].geom.type
        : null;
    }
  },
  created() {
    const data = this.$store.state.book.activeTable;
    const dataArray = Object.keys(data.data);

    this.dataLength = dataArray.length;

    if (data.fields.length) {
      this.dsInfo.name = data.name;
      this.dsInfo.geomType = this.getGeomType();
      this.dsInfo.length = data.dataLength;
      this.setTableData(data);
    }
  },
  mounted() {
    this.myInstance = this.$refs.tableComponent.hotInstance;
  }
};
